// Notification settings data layer: the resolved matrix the Notifications page
// renders, and the ONE write it issues.
//
// `GET /v1/me/notification-preferences` answers the caller's topics with their
// tier, each cell's state and lock FOR THE CALLER'S ROLE, the email timing, the
// preset the switches currently equal (or null) and the caller's own address.
// `PUT` on the same path takes a preset, a timing, or a list of cells in one
// transaction and answers the same shape; a locked or unavailable cell anywhere
// in the body refuses the whole write with a 422.
//
// The page never patches from the PUT's answer: every write is followed by a
// re-read (`apps/dashboard/CLAUDE.md` § Boundary).

import type {
  EmailCadence,
  NotificationChannel,
  NotificationKind,
  NotificationPreset,
  NotificationTier,
} from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { z } from 'zod';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { SESSION_TOKEN } from './session';

/**
 * The kinds and tiers the page shows: everything but the hidden tier's one
 * kind. `notification-labels.ts` owns the same two spellings for its label
 * records; they are restated here, identical, so the wire types do not hang off
 * a module that holds copy — the unions cannot drift apart without a compile
 * error at the first assignment between them.
 */
export type ShownKind = Exclude<NotificationKind, 'platform_notice'>;
export type ShownTier = Exclude<NotificationTier, 'hidden'>;

/** One resolved cell. */
export type NotificationSettingsCell = {
  readonly channel: NotificationChannel;
  readonly enabled: boolean;
  /** Locked on for the caller's role: shown as a lock and a reason, never a switch. */
  readonly mandatory: boolean;
  readonly is_override: boolean;
  readonly default_enabled: boolean;
  /** False for a webhook cell whose topic has no outbound event. */
  readonly available: boolean;
};

/** One topic. The server lists no hidden-tier topic, so both ids are the shown ones. */
export type NotificationSettingsRow = {
  readonly kind: ShownKind;
  readonly tier: ShownTier;
  readonly channels: readonly NotificationSettingsCell[];
};

export type NotificationSettings = {
  readonly matrix: readonly NotificationSettingsRow[];
  readonly cadence: EmailCadence;
  readonly preset: NotificationPreset | null;
  readonly delivery_address: string | null;
};

export type NotificationCellWrite = {
  readonly kind: NotificationKind;
  readonly channel: NotificationChannel;
  readonly enabled: boolean;
};

export type NotificationSettingsWrite = {
  readonly preset?: NotificationPreset;
  readonly cadence?: EmailCadence;
  readonly cells?: readonly NotificationCellWrite[];
};

export const NOTIFICATION_SETTINGS_QUERY_KEY = 'notification_settings';

const PATH = '/v1/me/notification-preferences';

/** The caller's settings. Disabled (idle) with no session, like `useMe`. */
export function useNotificationSettings(token: string | null = SESSION_TOKEN) {
  return createQuery<NotificationSettings>(() => ({
    queryKey: [NOTIFICATION_SETTINGS_QUERY_KEY, token, activeAccountId()],
    enabled: token !== null,
    queryFn: () => apiFetch<NotificationSettings>(PATH, token),
  }));
}

/** One atomic write: a preset, a timing, or a list of cells. */
export function replaceNotificationSettings(
  body: NotificationSettingsWrite,
  token: string | null = SESSION_TOKEN,
): Promise<NotificationSettings> {
  return apiFetch<NotificationSettings>(PATH, token, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/** `POST /v1/me/notification-test-email`: where the test mail was sent. */
const TestEmailSchema = z.object({ sent_to: z.string() });

export type TestEmailResult = z.infer<typeof TestEmailSchema>;

/**
 * Change only the delivery timing: one bulk PUT carrying `{ cadence }`. The
 * caller re-reads the settings; nothing here patches from the echo.
 */
export function setCadence(
  cadence: EmailCadence,
  token: string | null = SESSION_TOKEN,
): ReturnType<typeof replaceNotificationSettings> {
  return replaceNotificationSettings({ cadence }, token);
}

/**
 * Send one test email to the caller's own address. A refusal arrives as an
 * `ApiError` whose STATUS says which one: 422 no address, 409 the address
 * bounced, 429 over the hourly limit, 502 the mail could not be sent.
 * `NotificationTestEmail` maps those four to the page's own words.
 */
export async function sendTestEmail(
  token: string | null = SESSION_TOKEN,
): Promise<TestEmailResult> {
  return apiFetch(
    '/v1/me/notification-test-email',
    token,
    { method: 'POST' },
    { schema: TestEmailSchema },
  );
}
